import { useEffect, useMemo, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import styles from './FlowChat.module.css';

/**
 * Animated Lottie launcher button with rotating tooltip messages.
 * Click toggles the chat (controlled by parent via onClick + isOpen).
 */
const AnimatedChatButton = ({
  animationData,
  onClick,
  isOpen,
  hasBeenOpened,
  messages,
  color,
  side,
  bottomDesktop,
  bottomMobile,
  sizeDesktop,
  sizeMobile,
  sideOffsetDesktop,
  sideOffsetMobile,
}) => {
  const [currentText, setCurrentText] = useState('');
  const containerRef = useRef(null);
  const indexRef = useRef(0);
  const cycleCompletedRef = useRef(false);
  const autoMessagesShownRef = useRef(0);
  const timersRef = useRef([]);
  const spanVisibleRef = useRef(false);
  const isHoveringRef = useRef(false);

  // Timing config (ms)
  const INITIAL_DELAY = 8000;
  const VISIBLE_TIME = 2500;
  const GAP_TIME = 35000;
  const POST_OPEN_DELAY = 30 * 60 * 1000;
  const MAX_AUTO_MESSAGES = 2;

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };
  const addTimer = (id) => { timersRef.current.push(id); };

  const setSpanVisible = (visible) => {
    const span = containerRef.current?.querySelector('span');
    if (!span) return;
    spanVisibleRef.current = visible;
    span.classList.remove(styles.spanHidden, styles.spanVisible);
    span.classList.add(visible ? styles.spanVisible : styles.spanHidden);
  };

  // Main rotation cycle
  useEffect(() => {
    if (isOpen || !messages || messages.length === 0) {
      clearTimers();
      setSpanVisible(false);
      return undefined;
    }

    if (hasBeenOpened) {
      cycleCompletedRef.current = true;
    }

    const scheduleNext = (delay) => {
      if (autoMessagesShownRef.current >= MAX_AUTO_MESSAGES) return;
      addTimer(setTimeout(showNext, delay));
    };

    const showNext = () => {
      if (isHoveringRef.current) return;

      if (autoMessagesShownRef.current >= MAX_AUTO_MESSAGES) return;

      setCurrentText(messages[indexRef.current]);
      autoMessagesShownRef.current += 1;
      setSpanVisible(true);

      addTimer(setTimeout(() => {
        setSpanVisible(false);
        const nextIndex = indexRef.current + 1;

        if (nextIndex >= messages.length) {
          cycleCompletedRef.current = true;
          indexRef.current = 0;
        } else {
          indexRef.current = nextIndex;
        }

        if (autoMessagesShownRef.current >= MAX_AUTO_MESSAGES) return;

        scheduleNext(cycleCompletedRef.current ? POST_OPEN_DELAY : GAP_TIME);
      }, VISIBLE_TIME));
    };

    const firstDelay = hasBeenOpened ? POST_OPEN_DELAY : INITIAL_DELAY;
    scheduleNext(firstDelay);

    return clearTimers;
  }, [messages, isOpen, hasBeenOpened]);

  const handleMouseEnter = () => {
    if (isOpen || !messages || messages.length === 0) return;
    isHoveringRef.current = true;
    clearTimers();
    indexRef.current = 0;
    setCurrentText(messages[0]);
    setSpanVisible(true);
  };

  const handleMouseLeave = () => {
    if (isOpen || !messages || messages.length === 0) return;
    isHoveringRef.current = false;
    setSpanVisible(false);
    if (autoMessagesShownRef.current >= MAX_AUTO_MESSAGES) return;

    indexRef.current = Math.min(autoMessagesShownRef.current, Math.max(messages.length - 1, 0));

    const scheduleNext = (delay) => {
      if (autoMessagesShownRef.current >= MAX_AUTO_MESSAGES) return;
      addTimer(setTimeout(showNext, delay));
    };

    const showNext = () => {
      if (isHoveringRef.current) return;

      if (autoMessagesShownRef.current >= MAX_AUTO_MESSAGES) return;

      setCurrentText(messages[indexRef.current]);
      autoMessagesShownRef.current += 1;
      setSpanVisible(true);

      addTimer(setTimeout(() => {
        setSpanVisible(false);
        const nextIndex = indexRef.current + 1;

        if (nextIndex >= messages.length) {
          cycleCompletedRef.current = true;
          indexRef.current = 0;
        } else {
          indexRef.current = nextIndex;
        }

        if (autoMessagesShownRef.current >= MAX_AUTO_MESSAGES) return;

        scheduleNext(cycleCompletedRef.current ? POST_OPEN_DELAY : GAP_TIME);
      }, VISIBLE_TIME));
    };

    scheduleNext(GAP_TIME);
  };

  const handleClick = () => {
    clearTimers();
    setSpanVisible(false);
    onClick?.();
  };

  if (!animationData) return null;

  const launcherStyle = {
    ...(color ? { '--chat-accent': color } : null),
    ...(bottomDesktop ? { '--chat-bottom-desktop': bottomDesktop } : null),
    ...(bottomMobile ? { '--chat-bottom-mobile': bottomMobile } : null),
    ...(sizeDesktop ? { '--chat-size-desktop': sizeDesktop } : null),
    ...(sizeMobile ? { '--chat-size-mobile': sizeMobile } : null),
    ...(sideOffsetDesktop ? { '--chat-side-desktop': sideOffsetDesktop } : null),
    ...(sideOffsetMobile ? { '--chat-side-mobile': sideOffsetMobile } : null),
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.launcher} ${isOpen ? styles.launcherOpen : ''}`}
      data-side={side === 'left' ? 'left' : 'right'}
      style={launcherStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className={styles.spanHidden}>{currentText}</span>
      <Lottie
        animationData={animationData}
        className={styles.lottie}
        loop
        onClick={handleClick}
      />
    </div>
  );
};

const FlowChat = ({ companyInfo }) => {
  const chatbot = companyInfo?.chatbot;
  const [BubbleChatComp, setBubbleChatComp] = useState(null);
  const [animationData, setAnimationData] = useState(null);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  // Lazy load BubbleChat once chatbot config is available
  useEffect(() => {
    if (!chatbot?.enabled || !chatbot?.flowId) return;
    let cancelled = false;
    import('flowise-embed-react').then((mod) => {
      if (!cancelled) setBubbleChatComp(() => mod.BubbleChat);
    });
    return () => { cancelled = true; };
  }, [chatbot?.enabled, chatbot?.flowId]);

  // Load Lottie animation JSON from /public
  useEffect(() => {
    let cancelled = false;
    fetch('/animations/Chatbot_Off_v2.json')
      .then((res) => res.json())
      .then((json) => { if (!cancelled) setAnimationData(json); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Continuously hide native button + tag bubbles/input with our classes
  // and force the site body font everywhere inside the chatbot.
  useEffect(() => {
    if (!BubbleChatComp) return undefined;

    const applyCustomStyles = () => {
      const flowiseElement = document.querySelector('flowise-chatbot');

      // Hide original Flowise button (in shadowRoot if present)
      if (flowiseElement?.shadowRoot) {
        const originalButton = flowiseElement.shadowRoot.querySelector('[part="button"]');
        if (originalButton) {
          originalButton.style.display = 'none';
          originalButton.style.visibility = 'hidden';
          originalButton.style.opacity = '0';
          originalButton.style.pointerEvents = 'none';
          originalButton.style.position = 'absolute';
          originalButton.style.left = '-9999px';
          originalButton.style.top = '-9999px';
        }

        const buttonSelectors = [
          '.bubble-button',
          '.chat-button',
          '.chatbot-button',
          'button[data-testid*="button"]',
          'button[aria-label*="chat"]',
          'div[role="button"]',
        ];
        buttonSelectors.forEach((selector) => {
          flowiseElement.shadowRoot.querySelectorAll(selector).forEach((b) => {
            b.style.display = 'none';
            b.style.visibility = 'hidden';
            b.style.opacity = '0';
            b.style.pointerEvents = 'none';
          });
        });
      }

      // Also try main DOM
      [
        'flowise-chatbot [part="button"]',
        '.bubble-button',
        '.chat-button',
        '.chatbot-button',
      ].forEach((selector) => {
        document.querySelectorAll(selector).forEach((b) => {
          b.style.display = 'none';
          b.style.visibility = 'hidden';
          b.style.opacity = '0';
          b.style.pointerEvents = 'none';
        });
      });

      // Tag chat container / bubbles / input with our class names
      const chatContainer =
        document.querySelector('[data-testid="chat-container"]') ||
        document.querySelector('.chat-container') ||
        document.querySelector('.chatbot-container');
      if (chatContainer) chatContainer.classList.add('chatbot-container');

      document
        .querySelectorAll('[data-testid="host-bubble"], .host-bubble')
        .forEach((b) => b.classList.add('chatbot-host-bubble'));
      document
        .querySelectorAll('[data-testid="guest-bubble"], .guest-bubble')
        .forEach((b) => b.classList.add('chatbot-guest-bubble'));

      const chatInput = document.querySelector(
        '[data-testid="chat-input"], .chat-input, input[type="text"], textarea'
      );
      if (chatInput) chatInput.classList.add('chatbot-input');

      // Force Poppins everywhere chat-related
      const applyPoppins = (el) => { el.style.fontFamily = 'Poppins'; };
      const fontSelectors = [
        'flowise-chatbot',
        'flowise-chatbot *',
        '.chatbot-container',
        '.chatbot-container *',
        '[data-testid*="chat"]',
        '[data-testid*="chat"] *',
        '.chat-window',
        '.chat-window *',
        '.bubble-chat',
        '.bubble-chat *',
        '[data-modal="chatbot"]',
        '[data-modal="chatbot"] *',
        '.flowise-fullchatbot',
        '.flowise-fullchatbot *',
        '[role="dialog"]',
        '[role="dialog"] *',
        '.chatbot-widget',
        '.chatbot-widget *',
        '[data-testid*="message"]',
        '[data-testid*="message"] *',
        '.message-content',
        '.message-content *',
        '.bot-message',
        '.bot-message *',
        '.user-message',
        '.user-message *',
      ];
      fontSelectors.forEach((selector) => {
        try {
          document.querySelectorAll(selector).forEach(applyPoppins);
        } catch (e) {
          // ignore invalid selectors
        }
      });

      if (flowiseElement?.shadowRoot) {
        flowiseElement.shadowRoot.querySelectorAll('*').forEach(applyPoppins);
        flowiseElement.shadowRoot.host.style.fontFamily = 'Poppins';
      }

      // Style the footer "Powered by ..." span (flowise injects inline bg)
      const styleFooter = (root) => {
        if (!root) return;
        root
          .querySelectorAll('.chatbot-container span.w-full.text-center, span.w-full.text-center.m-auto')
          .forEach((el) => {
            el.style.setProperty('color', '#ffffff', 'important');
            el.style.setProperty(
              'background',
              'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
              'important'
            );
            el.style.setProperty('background-color', 'transparent', 'important');
            el.style.setProperty('backdrop-filter', 'blur(20px) saturate(180%)', 'important');
            el.style.setProperty('-webkit-backdrop-filter', 'blur(20px) saturate(180%)', 'important');
            el.style.setProperty('font-family', 'Poppins, sans-serif', 'important');
            el.style.setProperty('border', 'none', 'important');
            el.querySelectorAll('a').forEach((a) => {
              a.style.setProperty('color', '#ffffff', 'important');
              a.style.setProperty('font-family', 'Poppins, sans-serif', 'important');
            });
          });
      };
      styleFooter(document);
      if (flowiseElement?.shadowRoot) styleFooter(flowiseElement.shadowRoot);
    };

    applyCustomStyles();
    const observer = new MutationObserver(applyCustomStyles);
    observer.observe(document.body, { childList: true, subtree: true });
    const interval = setInterval(applyCustomStyles, 1000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [BubbleChatComp]);

  const messages = useMemo(() => {
    if (!Array.isArray(chatbot?.mensajesRotativos)) return [];
    return chatbot.mensajesRotativos.map((m) => m?.text).filter(Boolean);
  }, [chatbot?.mensajesRotativos]);

  if (!chatbot?.enabled || !chatbot?.flowId) return null;

  const accent = chatbot.color || '#ea6387';
  const textColor = chatbot.textColor || '#ffffff';
  const companyName = companyInfo?.companyName || 'Koppi';

  // Liquid Glass theme (mirrors LST SmartChatbot)
  const chatTheme = {
    button: {
      backgroundColor: accent,
      dragAndDrop: false,
      size: 0,
      iconColor: 'white',
      right: -100,
      bottom: -100,
    },
    tooltip: { showTooltip: false },
    customCSS: `/* Ocultar completamente el botón original de Flowise */
      [part="button"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        width: 0 !important;
        height: 0 !important;
      }

      /* FONT OVERRIDE - Poppins en todos los elementos del chatbot */
      flowise-chatbot *,
      flowise-chatbot,
      .chatbot-container *,
      .chatbot-container,
      [data-testid*="chat"] *,
      [data-testid*="chat"],
      .chat-window *,
      .chat-window,
      .bubble-chat *,
      .bubble-chat {
        font-family: 'Poppins', sans-serif !important;
      }

      /* Contenedor específico del input */
      .chatbot-container .w-full.px-5.pt-2.pb-1 {
        border-top: 2px solid rgba(255, 255, 255, 0.2) !important;
        padding-right: 20px !important;
        padding-left: 0px !important;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05)) !important;
        backdrop-filter: blur(20px) saturate(180%) !important;
        -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
        font-family: 'Poppins', sans-serif !important;
      }

      /* Campo de entrada - Liquid Glass */
      .chatbot-input {
        border-radius: 24px !important;
        margin: 10px !important;
        background: linear-gradient(135deg,
          rgba(255, 255, 255, 0.15) 0%,
          rgba(255, 255, 255, 0.08) 50%,
          rgba(255, 255, 255, 0.12) 100%) !important;
        backdrop-filter: blur(25px) saturate(200%) brightness(1.1) !important;
        -webkit-backdrop-filter: blur(25px) saturate(200%) brightness(1.1) !important;
        border: 1px solid rgba(255, 255, 255, 0.25) !important;
        box-shadow:
          0 8px 32px rgba(0, 0, 0, 0.12),
          inset 0 1px 0 rgba(255, 255, 255, 0.3),
          0 0 0 0.5px rgba(255, 255, 255, 0.1) !important;
        padding: 1px !important;
        color: white !important;
        overflow: hidden !important;
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
        position: relative !important;
        font-family: 'Poppins', sans-serif !important;
      }

      /* Botones bubble visibles excepto el de envío */
      .bubble-button:not(.flowise-chat-input-send-button),
      .chat-button:not(.flowise-chat-input-send-button),
      .chatbot-button:not(.flowise-chat-input-send-button),
      button[data-testid*="bubble"]:not([data-testid*="send"]),
      button[aria-label*="chat"]:not([aria-label*="send"]),
      div[role="button"]:not(.flowise-chat-input-send-button) {
        display: flex !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
      }

      /* Estados del input */
      .chatbot-input:hover,
      .chatbot-input:focus-within {
        border-color: ${accent}99 !important;
      }

      /* Ocultar scrollbar */
      .chatbot-input::-webkit-scrollbar { display: none !important; }
      .chatbot-input textarea {
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
        background: transparent !important;
        position: relative !important;
        z-index: 2 !important;
        font-family: 'Poppins', sans-serif !important;
      }
      .chatbot-input textarea::-webkit-scrollbar { display: none !important; }

      /* Placeholder en blanco */
      .chatbot-input textarea::placeholder,
      .chatbot-input input::placeholder,
      .chatbot-container textarea::placeholder,
      .chatbot-container input::placeholder {
        color: #ffffff !important;
        opacity: 0.7 !important;
        font-family: 'Poppins', sans-serif !important;
      }
      .chatbot-input textarea::-webkit-input-placeholder,
      .chatbot-container textarea::-webkit-input-placeholder {
        color: #ffffff !important;
        opacity: 0.7 !important;
      }

      /* Burbujas del bot */
      .chatbot-host-bubble {
        border-radius: 20px !important;
        padding: 12px 16px !important;
        max-width: 85% !important;
        margin-bottom: 8px !important;
        background-color: rgba(248, 248, 250, 0) !important;
        backdrop-filter: blur(10px) !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.36) !important;
        color: white !important;
        border: 1px solid rgba(255, 255, 255, 0) !important;
        font-family: 'Poppins', sans-serif !important;
      }

      /* Burbujas del usuario */
      .chatbot-guest-bubble {
        border-radius: 20px !important;
        padding: 12px 16px !important;
        max-width: 85% !important;
        margin-bottom: 8px !important;
        background-color: ${accent} !important;
        backdrop-filter: blur(10px) !important;
        color: white !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
        border: 1px solid rgba(255, 255, 255, 0.2) !important;
        font-family: 'Poppins', sans-serif !important;
      }

      /* Contenedor del chat */
      .chatbot-container {
        border-radius: 10px !important;
        overflow: hidden !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        background-color: rgba(0, 0, 0, 0.3) !important;
        backdrop-filter: blur(15px) !important;
        border: none !important;
        font-family: 'Poppins', sans-serif !important;
      }

      /* Footer "Powered by koppi" - Liquid Glass */
      .chatbot-container span.w-full.text-center,
      .chatbot-container .w-full.text-center.px-\[10px\] {
        color: rgb(255, 255, 255) !important;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05)) !important;
        backdrop-filter: blur(20px) saturate(180%) !important;
        -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
        font-family: 'Poppins', sans-serif !important;
        border: none !important;
      }

      .chatbot-container span.w-full.text-center a,
      .chatbot-container .w-full.text-center a {
        color: rgb(255, 255, 255) !important;
        font-family: 'Poppins', sans-serif !important;
      }

      .chat-messages-container {
        scroll-behavior: auto !important;
        background-color: transparent !important;
        font-family: 'Poppins', sans-serif !important;
      }

      /* Garantizar Poppins */
      .flowise-fullchatbot *,
      .flowise-fullchatbot,
      [data-modal="chatbot"] *,
      [data-modal="chatbot"],
      .chat-bubble *,
      .chat-bubble,
      .chat-input *,
      .chat-input,
      [role="dialog"] *,
      [role="dialog"],
      .chatbot-widget *,
      .chatbot-widget,
      [data-testid*="message"] *,
      [data-testid*="message"],
      .message-content *,
      .message-content,
      .bot-message *,
      .bot-message,
      .user-message *,
      .user-message,
      [data-testid*="button"] *,
      [data-testid*="button"],
      .send-button *,
      .send-button,
      .attachment-button *,
      .attachment-button {
        font-family: 'Poppins', sans-serif !important;
      }
    `,
    chatWindow: {
      showTitle: true,
      showAgentMessages: false,
      title: chatbot.titulo || `Asistente ${companyName}`,
      titleAvatarSrc: '',
      welcomeMessage:
        chatbot.bienvenida ||
        `¡Hola! Soy tu asistente inteligente de ${companyName}. Estoy aquí para ayudarte con cualquier duda sobre nuestras propiedades, financiamiento, ubicaciones y más. ¿En qué puedo ayudarte hoy?`,
      errorMessage: 'Disculpa, tuve un problema técnico. ¿Podrías intentar de nuevo?',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      width: 470,
      fontSize: 15,
      starterPrompts: [],
      starterPromptFontSize: 15,
      clearChatOnReload: false,
      sourceDocsTitle: 'Fuentes:',
      renderHTML: true,
      botMessage: {
        backgroundColor: 'rgba(247, 248, 255, 0.1)',
        textColor: '#ffffff',
        showAvatar: false,
        fontFamily: 'Poppins',
      },
      userMessage: {
        backgroundColor: accent,
        textColor: '#ffffff',
        showAvatar: true,
        avatarSrc:
          'https://raw.githubusercontent.com/zahidkhawaja/langchain-chat-nextjs/main/public/usericon.png',
        fontFamily: 'Poppins',
      },
      textInput: {
        placeholder: chatbot.placeholderInput || 'Escribe tu pregunta aquí...',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        textColor: '#ffffff',
        sendButtonColor: 'white',
        maxChars: 500,
        maxCharsWarningMessage: 'Has excedido el límite de caracteres',
        autoFocus: true,
        fontFamily: 'Poppins',
      },
      feedback: {
        color: '#ffffff',
        fontFamily: 'Poppins',
      },
      dateTimeToggle: {
        date: true,
        time: true,
      },
      footer: {
        textColor: '#ffffff',
        text: 'Powered by',
        company: 'koppi',
        companyLink: 'https://koppi.mx',
        fontFamily: 'Poppins',
      },
    },
  };

  // Programmatically open the BubbleChat by clicking its hidden native button
  const handleToggle = () => {
    if (!hasBeenOpened) setHasBeenOpened(true);
    setTimeout(() => {
      const flowiseElement = document.querySelector('flowise-chatbot');
      if (!flowiseElement) return;
      if (flowiseElement.shadowRoot) {
        const btn = flowiseElement.shadowRoot.querySelector('[part="button"]');
        if (btn && typeof btn.click === 'function') {
          btn.click();
          return;
        }
      }
      const flowiseAny = flowiseElement;
      if (typeof flowiseAny.toggle === 'function') flowiseAny.toggle();
      else if (typeof flowiseAny.open === 'function') flowiseAny.open();
      else if (typeof flowiseAny.show === 'function') flowiseAny.show();
    }, 100);
  };

  return (
    <>
      <AnimatedChatButton
        animationData={animationData}
        onClick={handleToggle}
        isOpen={false}
        hasBeenOpened={hasBeenOpened}
        messages={messages}
        color={accent}
        side={chatbot.ladoDerecho === false ? 'left' : 'right'}
        bottomDesktop={chatbot.posicionBottomDesktop}
        bottomMobile={chatbot.posicionBottomMobile}
        sizeDesktop={chatbot.tamanoBotonDesktop}
        sizeMobile={chatbot.tamanoBotonMobile}
      />
      {BubbleChatComp && (
        <BubbleChatComp
          chatflowid={chatbot.flowId}
          apiHost={chatbot.apiHost || 'https://ecoflow.koppi.mx'}
          chatflowConfig={{
            sessionId: `koppi-${Date.now()}`,
            vars: {
              companyName,
              currentPage: typeof window !== 'undefined' ? window.location.pathname : '/',
              userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
            },
          }}
          theme={chatTheme}
        />
      )}
    </>
  );
};

export default FlowChat;
